/**
 * SunoMidiJobsContext — фоновые MIDI-генерации.
 *
 * Источник истины для has_midi и самих данных — backend (task_events +
 * /tracks/:id/midi endpoint). localStorage НЕ используется для хранения
 * результатов (универсальность на всех устройствах, правило проекта).
 *
 * Что живёт в localStorage:
 *   - ТОЛЬКО pending-jobs (UI-state): нужен чтобы polling переживал
 *     refresh страницы — это эфемерная transient информация, не данные.
 *
 * Session-state (useState, живёт до перезагрузки):
 *   - In-memory cache результатов, заполняется по мере того как юзер смотрит
 *     MIDI. Перезагрузка очищает кэш → повторный клик идёт в БД снова.
 *
 * API:
 *   - start(track)             — запустить генерацию
 *   - isRunning(id)            — идёт ли polling
 *   - getCached(id)            — session-cache (true если в этой сессии уже смотрели)
 *   - pendingViewer            — если job завершился — data для viewer'а
 *   - dismissViewer()          — закрыть viewer
 *   - openCached(id, title)    — открыть из session-кэша (sync, без сети)
 *   - openFromBackend(...)     — открыть с lazy-fetch из /tracks/:id/midi
 *
 * На успешное завершение polling'а диспатчится window-event `suno-midi-ready`
 * — BotsAudioLibrary/Album слушают и перезапрашивают /tracks чтобы бейдж
 * появился на строке.
 */
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from 'react'
import { generateMidi, getTrackMidi, type SunoTrack } from '../api/music'
import { type TaskStatusResponse } from '../api/images'
import { waitForTask } from '../pages/generate/utils/waitForTask'
import { showToast } from '../components/Toast'
import { translateSunoError } from '../pages/generate/utils/sunoErrors'

const LS_PENDING_KEY = 'suno_midi_pending_v1'
const POLL_INTERVAL_MS = 10_000

interface PendingMidiJob {
	trackId: string
	requestId: string
	title: string | null
	startedAt: number
}

interface CachedMidi {
	trackId: string
	title: string | null
	midiData: Record<string, unknown>
	createdAt: number
}

export interface MidiViewerState {
	trackId: string
	title: string | null
	midiData: Record<string, unknown>
	/** Готовые .mid-файлы (Mureka отдаёт архив, а не JSON-ноты). */
	files?: { name: string; url: string }[]
}

interface SunoMidiJobsValue {
	start: (track: SunoTrack) => void
	isRunning: (trackId: string) => boolean
	getCached: (trackId: string) => Record<string, unknown> | null
	pendingViewer: MidiViewerState | null
	dismissViewer: () => void
	/** Открыть viewer для закэшированного MIDI (локальный кэш — без сети). */
	openCached: (trackId: string, title: string | null) => boolean
	/** Открыть viewer, подтянув данные из БД если нет в локальном кэше.
	 *  Используется по клику на MIDI-бейдж в SunoTrackList — flag has_midi
	 *  из /tracks подтверждает что данные есть на бэке. */
	openFromBackend: (trackId: string, title: string | null) => Promise<boolean>
}

const SunoMidiJobsContext = createContext<SunoMidiJobsValue | null>(null)

// ─── localStorage helpers (ТОЛЬКО для pending-jobs, не для данных) ─
function loadPending(): Record<string, PendingMidiJob> {
	try {
		const raw = localStorage.getItem(LS_PENDING_KEY)
		if (!raw) return {}
		const parsed = JSON.parse(raw)
		if (!parsed || typeof parsed !== 'object') return {}
		const filtered: Record<string, PendingMidiJob> = {}
		for (const [k, v] of Object.entries(parsed as Record<string, PendingMidiJob>)) {
			if (v && typeof v === 'object' && v.requestId && v.startedAt) {
				// Запуск с номером задачи живёт, пока сервер не ответил (план «Ждём ответа нейросети», Ф3): прежние
				// 30 минут бросали следить за долгой задачей после перезагрузки.
				filtered[k] = v
			}
		}
		return filtered
	} catch {
		return {}
	}
}
function savePending(pending: Record<string, PendingMidiJob>) {
	try {
		localStorage.setItem(LS_PENDING_KEY, JSON.stringify(pending))
	} catch { /* ignore — best-effort persist */ }
}

/** Вытаскивает midi_data из разных shape'ов task response. */
function extractMidiFromTaskStatus(s: TaskStatusResponse): Record<string, unknown> | null {
	const rd = s.response_data
	if (!rd || typeof rd !== 'object') return null
	const d = rd as Record<string, unknown>
	// Worker сохраняет: {midi_instruments: N, midi_data: {instruments: [...]}}
	// Adapter recovery: {midi_data_immediate: {...}}
	const candidates = [
		d.midi_data,
		d.midiData,
		d.midi_data_immediate,
		d,
	]
	for (const c of candidates) {
		if (c && typeof c === 'object') {
			const obj = c as Record<string, unknown>
			if (Array.isArray(obj.instruments) || Array.isArray(obj.tracks)) {
				return obj
			}
		}
	}
	return null
}

export function SunoMidiJobsProvider({ children }: { children: ReactNode }) {
	// In-memory session cache — не persistent. Очищается на reload (тогда
	// повторный клик пойдёт в БД, что правильно — источник истины там).
	const [cache, setCache] = useState<Record<string, CachedMidi>>({})
	const [runningIds, setRunningIds] = useState<Set<string>>(
		() => new Set(Object.keys(loadPending())),
	)
	const [pendingViewer, setPendingViewer] = useState<MidiViewerState | null>(null)
	const activeRef = useRef<Set<string>>(new Set())
	const pendingRef = useRef<Record<string, PendingMidiJob>>(loadPending())

	const writeCache = useCallback((trackId: string, title: string | null, midiData: Record<string, unknown>) => {
		setCache((prev) => ({
			...prev,
			[trackId]: { trackId, title, midiData, createdAt: Date.now() },
		}))
	}, [])

	const markRunning = useCallback((trackId: string, running: boolean) => {
		setRunningIds((prev) => {
			const next = new Set(prev)
			if (running) next.add(trackId)
			else next.delete(trackId)
			return next
		})
	}, [])

	const setPendingJob = useCallback((trackId: string, job: PendingMidiJob | null) => {
		if (job) pendingRef.current[trackId] = job
		else delete pendingRef.current[trackId]
		savePending({ ...pendingRef.current })
	}, [])

	const publishViewer = useCallback((state: MidiViewerState | null) => {
		setPendingViewer(state)
	}, [])

	const runPolling = useCallback(
		async (trackId: string, requestId: string, trackTitle: string | null) => {
			if (activeRef.current.has(trackId)) return
			activeRef.current.add(trackId)
			markRunning(trackId, true)
			setPendingJob(trackId, {
				trackId,
				requestId,
				title: trackTitle,
				startedAt: pendingRef.current[trackId]?.startedAt ?? Date.now(),
			})

			try {
				// Ждём ответа сервера без потолка (waitForTask, план «Ждём ответа нейросети», Ф3).
				const s = await waitForTask(requestId, {
					minDelayMs: POLL_INTERVAL_MS,
					startedAt: pendingRef.current[trackId]?.startedAt,
				})
				if (!s) return
				if (s.status === 'failed') {
					showToast(translateSunoError(s.error_message_ru || s.error_message, 'midi'), 'error')
					return
				}
				const midiData = extractMidiFromTaskStatus(s)
				if (!midiData) {
					showToast('Suno завершил задачу без MIDI-данных — попробуй ещё раз.', 'error')
					return
				}

				writeCache(trackId, trackTitle, midiData)
				showToast('MIDI готов', 'success')
				publishViewer({ trackId, title: trackTitle, midiData })
				// Уведомляем список треков — теперь у трека has_midi=true
				// на бэке, /tracks надо перезапросить чтобы бейдж MIDI
				// появился на строке автоматически.
				window.dispatchEvent(
					new CustomEvent('suno-midi-ready', { detail: { trackId } }),
				)
			} finally {
				activeRef.current.delete(trackId)
				markRunning(trackId, false)
				setPendingJob(trackId, null)
			}
		},
		[markRunning, publishViewer, setPendingJob, writeCache],
	)

	const start = useCallback(
		async (track: SunoTrack) => {
			const trackId = track.id

			// Есть кэш — сразу открываем viewer.
			const cached = cache[trackId]
			if (cached) {
				publishViewer({ trackId, title: track.title, midiData: cached.midiData })
				return
			}

			if (activeRef.current.has(trackId)) {
				showToast('Генерация MIDI уже идёт...', 'info')
				return
			}

			showToast(`Генерируем MIDI для «${track.title?.trim() || 'трека'}»...`, 'info')

			const res = await generateMidi({ track_id: trackId })

			// Recovery-immediate из backend'а
			if (res.ok && (res as unknown as { midi_data?: Record<string, unknown> }).midi_data) {
				const md = (res as unknown as { midi_data: Record<string, unknown> }).midi_data
				writeCache(trackId, track.title, md)
				showToast('MIDI уже был готов', 'success')
				publishViewer({ trackId, title: track.title, midiData: md })
				window.dispatchEvent(
					new CustomEvent('suno-midi-ready', { detail: { trackId } }),
				)
				return
			}

			if (!res.ok) {
				const friendly = translateSunoError(res.error, 'midi')
				showToast(friendly, 'error')
				// Бэк пометил midi-request failed для этого трека → кабинет прячет
				// кнопку «Ноты» (заранее не предскажешь, но повторять смысла нет).
				window.dispatchEvent(new CustomEvent('suno-midi-failed', { detail: { trackId } }))
				return
			}

			if (!res.request_id) {
				showToast('Не удалось запустить генерацию MIDI', 'error')
				return
			}

			runPolling(trackId, res.request_id, track.title)
		},
		[cache, publishViewer, runPolling, writeCache],
	)

	const openCached = useCallback(
		(trackId: string, title: string | null): boolean => {
			const cached = cache[trackId]
			if (!cached) return false
			publishViewer({ trackId, title, midiData: cached.midiData })
			return true
		},
		[cache, publishViewer],
	)

	const openFromBackend = useCallback(
		async (trackId: string, title: string | null): Promise<boolean> => {
			// Session-cache — если уже смотрели в этой сессии, открываем мгновенно.
			if (openCached(trackId, title)) return true
			try {
				const res = await getTrackMidi(trackId)
				// У Mureka JSON-нот нет — приходят файлы. Открываем вьюер и с ними,
				// иначе ноты этого провайдера были бы недостижимы.
				if (!res.ok || (!res.midi_data && !res.files?.length)) return false
				publishViewer({ trackId, title, midiData: res.midi_data ?? {}, files: res.files })
				if (res.midi_data) writeCache(trackId, title, res.midi_data)
				return true
			} catch {
				return false
			}
		},
		[openCached, publishViewer, writeCache],
	)

	const getCached = useCallback(
		(trackId: string): Record<string, unknown> | null => {
			return cache[trackId]?.midiData ?? null
		},
		[cache],
	)

	const dismissViewer = useCallback(() => {
		publishViewer(null)
	}, [publishViewer])

	// Restore pending на reload
	useEffect(() => {
		const pending = pendingRef.current
		const ids = Object.keys(pending)
		if (ids.length === 0) return
		const t = setTimeout(() => {
			for (const trackId of ids) {
				const job = pending[trackId]
				if (!job) continue
				runPolling(trackId, job.requestId, job.title)
			}
		}, 500)
		return () => clearTimeout(t)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const isRunning = useCallback((trackId: string) => runningIds.has(trackId), [runningIds])

	const value = useMemo<SunoMidiJobsValue>(
		() => ({ start, isRunning, getCached, pendingViewer, dismissViewer, openCached, openFromBackend }),
		[start, isRunning, getCached, pendingViewer, dismissViewer, openCached, openFromBackend],
	)

	return (
		<SunoMidiJobsContext.Provider value={value}>{children}</SunoMidiJobsContext.Provider>
	)
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSunoMidiJobs(): SunoMidiJobsValue {
	const ctx = useContext(SunoMidiJobsContext)
	if (!ctx) {
		return {
			start: () => {},
			isRunning: () => false,
			getCached: () => null,
			pendingViewer: null,
			dismissViewer: () => {},
			openCached: () => false,
			openFromBackend: async () => false,
		}
	}
	return ctx
}
